<script setup lang="ts">
definePageMeta({
	layout: 'area-riservata',
	middleware: 'auth',
});

interface GoalPath {
	id?: number;
	title?: string | null;
	color?: string | null;
}

interface GoalItem {
	id: number;
	icon?: string | null;
	title?: string | null;
	description?: string | null;
	completion_percentage?: number | null;
	completion_status?: 'in-progress' | 'completed' | null;
	expected_completion_date?: string | null;
	path?: GoalPath | number | null;
}

interface AppointmentItem {
	id: number;
	start_datetime: string;
	end_datetime?: string | null;
	customer_end_datetime?: string | null;
	type?: string | null;
	status?: string | null;
	color?: string | null;
	format?: 'in-person' | 'phone' | 'online' | null;
	online_url?: string | null;
	purchased_service?: {
		service?: { name?: string | null } | null;
		used_quantity?: number | null;
		purchased_quantity?: number | null;
	} | null;
	location?: {
		name?: string | null;
		address?: { street?: string | null; city?: { name?: string | null } | null } | null;
	} | number | null;
	staff?: {
		name?: string | null;
		surname?: string | null;
		public_avatar?: string | { id: string } | null;
	} | number | null;
}

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const { accessToken, user, customer } = useAuth();
	const { authFetch } = useAuthFetch();

const displayName = computed(() => {
	if (!user.value) return '';
	const { first_name, email } = user.value;
	if (first_name?.trim()) return first_name.trim();
	return email || 'Utente';
});
const welcomeWord = computed(() => {
	const g = customer.value?.gender;
	if (g === 'female') return 'Benvenuta';
	if (g === 'male') return 'Benvenuto';
	return 'Ciao';
});
const goals = ref<GoalItem[]>([]);
const goalsLoading = ref(true);
const appointments = ref<AppointmentItem[]>([]);
const appointmentsLoading = ref(false);
const showPastGoals = ref(false);

const config = useRuntimeConfig();
const { data: siteData } = await useFetch<{ globals?: { logo?: string | null } }>('/api/site-data', { key: 'site-data' });
const dashboardLogoUrl = computed(() => {
	if (!siteData.value?.globals?.logo || !config.public.directusUrl) return '/images/logo.svg';
	return `${config.public.directusUrl}/assets/${siteData.value.globals.logo}`;
});

const goalsInProgress = computed(() =>
	goals.value.filter(g => g.completion_status !== 'completed')
);
const goalsCompleted = computed(() =>
	goals.value.filter(g => g.completion_status === 'completed')
);
const hasCompletedGoals = computed(() => goalsCompleted.value.length > 0);

// Calendario: buffer di 6 settimane, visibili solo le 2 centrali; scorrimento fluido e recenter dopo animazione
const selectedDate = ref(new Date());

/** Lunedì della settimana per una data (Lun = 1). */
function getWeekMonday(d: Date): Date {
	const x = new Date(d);
	const day = x.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	x.setDate(x.getDate() + diff);
	x.setHours(0, 0, 0, 0);
	return x;
}

/** Inizio del buffer di 6 settimane (lunedì della prima settimana). */
const bufferStart = ref<Date>(getInitialBufferStart());

function getInitialBufferStart(): Date {
	const mon = getWeekMonday(selectedDate.value);
	const d = new Date(mon);
	d.setDate(mon.getDate() - 14); // 2 settimane indietro → le 2 centrali contengono la data corrente
	d.setHours(0, 0, 0, 0);
	return d;
}

/** Indice del blocco visibile: 0 = prime 2 settimane, 1 = centrali, 2 = ultime 2. Mostriamo solo 2 settimane alla volta. */
const visibleBlockIndex = ref(1);
/** Ref dell'elemento che anima lo scorrimento, per ascoltare transitionend. */
const calendarStripRef = ref<HTMLElement | null>(null);
/** Quando true disabilita la transizione CSS (aggiornamento post-API/recenter senza animazione). */
const skipCalendarTransition = ref(false);
/** Timeout per il recenter, da annullare se l'utente scrolla di nuovo prima della fine. */
let scrollEndTimeoutId: ReturnType<typeof setTimeout> | null = null;
/** Ritardo dopo transitionend prima di recentrare (evita sostituzione troppo presto e flicker). */
const RECENTER_DELAY_MS = 80;

const bufferEnd = computed(() => {
	const d = new Date(bufferStart.value);
	d.setDate(d.getDate() + 41); // 6 settimane
	d.setHours(23, 59, 59, 999);
	return d;
});

/** Per compatibilità con titolo e API: le 2 settimane attualmente visibili. */
const calendarStart = computed(() => {
	const d = new Date(bufferStart.value);
	d.setDate(d.getDate() + visibleBlockIndex.value * 14);
	return d;
});

const calendarEnd = computed(() => {
	const d = new Date(calendarStart.value);
	d.setDate(d.getDate() + 13);
	d.setHours(23, 59, 59, 999);
	return d;
});

/** Data in formato YYYY-MM-DD in ora locale (per allineare richiesta API ai giorni visibili). */
function toLocalYMD(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

/** Per ogni giorno (YYYY-MM-DD) restituisce il colore del primo appuntamento della giornata (per orario). */
function firstAppointmentColorForDate(ymd: string): string | null {
	const ofDay = appointments.value
		.filter(a => a.start_datetime.startsWith(ymd))
		.sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
	const first = ofDay[0];
	if (!first?.color || !String(first.color).trim()) return null;
	const hex = String(first.color).trim();
	return hex.startsWith('#') ? hex : `#${hex}`;
}

/** Per ogni giorno: riepilogo appuntamenti per tipologia (icona + colore come nelle card). */
type DayAppointmentSummary = { type: string; count: number; color: string; icon: string };
type DayCell = { date: Date; dayNum: number; isSelected: boolean; isToday: boolean; hasAppointments: boolean; firstColor: string | null; appointmentsByType: DayAppointmentSummary[] };

/** Raggruppa appuntamenti del giorno per tipo e restituisce icona/colore (stessi delle card). */
function appointmentsByTypeForDate(ymd: string): DayAppointmentSummary[] {
	const ofDay = appointments.value
		.filter(a => a.start_datetime.startsWith(ymd))
		.map(a => a.type ?? '');
	const byType = new Map<string, number>();
	for (const t of ofDay) {
		const key = t || 'default';
		byType.set(key, (byType.get(key) ?? 0) + 1);
	}
	return Array.from(byType.entries()).map(([type]) => ({
		type,
		count: byType.get(type)!,
		color: appointmentTypeColor(type === 'default' ? null : type),
		icon: appointmentTypeIcon(type === 'default' ? null : type),
	}));
}

function buildDaysForRange(start: Date, numDays: number): DayCell[] {
	const days: DayCell[] = [];
	const selectedYmd = `${selectedDate.value.getFullYear()}-${String(selectedDate.value.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.value.getDate()).padStart(2, '0')}`;
	const today = new Date();
	const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
	const appointmentDates = new Set(
		appointments.value.map(a => a.start_datetime.slice(0, 10))
	);
	for (let i = 0; i < numDays; i++) {
		const d = new Date(start);
		d.setDate(start.getDate() + i);
		const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		days.push({
			date: d,
			dayNum: d.getDate(),
			isSelected: ymd === selectedYmd,
			isToday: ymd === todayYmd,
			hasAppointments: appointmentDates.has(ymd),
			firstColor: firstAppointmentColorForDate(ymd),
			appointmentsByType: appointmentsByTypeForDate(ymd),
		});
	}
	return days;
}

/** Tutte le 6 settimane del buffer (6 righe di 7 giorni). Per la striscia scorrevole. */
const calendarAllWeeks = computed(() => {
	const start = new Date(bufferStart.value);
	const allDays = buildDaysForRange(start, 42);
	const rows: DayCell[][] = [];
	for (let r = 0; r < 6; r++) {
		rows.push(allDays.slice(r * 7, (r + 1) * 7));
	}
	return rows;
});

/** Le 2 settimane attualmente visibili (2 righe), per titolo e logica. */
const calendarWeeks = computed(() => {
	const start = new Date(bufferStart.value);
	start.setDate(start.getDate() + visibleBlockIndex.value * 14);
	const days = buildDaysForRange(start, 14);
	return [days.slice(0, 7), days.slice(7, 14)];
});

const calendarTitle = computed(() => {
	const s = calendarStart.value;
	const e = new Date(s);
	e.setDate(e.getDate() + 13);
	const monthName = MONTHS[e.getMonth()] ?? 'Gennaio';
	return `${s.getDate()} - ${e.getDate()} ${monthName.toUpperCase()} ${e.getFullYear()}`;
});

const appointmentsForSelectedDay = computed(() => {
	const ymd = `${selectedDate.value.getFullYear()}-${String(selectedDate.value.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.value.getDate()).padStart(2, '0')}`;
	const list = appointments.value.filter(a => a.start_datetime.startsWith(ymd));
	return list;
});

const selectedDayLabel = computed(() => {
	const d = selectedDate.value;
	const today = new Date();
	if (d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()) {
		return 'In programma oggi';
	}
	return `In programma il ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
});

/** Chiave stabile per v-memo sulla card appuntamenti: evita re-render quando giorno e lista (ids) non cambiano. */
const selectedDayCardMemoKey = computed(() => {
	const ymd = `${selectedDate.value.getFullYear()}-${String(selectedDate.value.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.value.getDate()).padStart(2, '0')}`;
	const ids = appointmentsForSelectedDay.value.map(a => a.id).join(',');
	return [ymd, ids, appointmentsLoading.value] as const;
});

async function loadGoals() {
	goalsLoading.value = true;
	if (!accessToken.value && !(process.client && localStorage.getItem('directus_access_token'))) {
		goalsLoading.value = false;
		return;
	}
	try {
		const res = await authFetch<{ goals: GoalItem[] }>('/api/area-riservata/goals');
		goals.value = res?.goals ?? [];
	} catch {
		goals.value = [];
	} finally {
		goalsLoading.value = false;
	}
}

let appointmentsFetchId = 0;

async function loadAppointments(
	overrideRange?: { from: string; to: string },
	options?: { silent?: boolean }
) {
	const from = overrideRange?.from ?? toLocalYMD(bufferStart.value);
	const to = overrideRange?.to ?? toLocalYMD(bufferEnd.value);
	const thisFetchId = ++appointmentsFetchId;
	const silent = options?.silent ?? false;

	if (!silent) appointmentsLoading.value = true;
	if (!accessToken.value && !(process.client && localStorage.getItem('directus_access_token'))) {
		if (!silent) appointmentsLoading.value = false;
		return;
	}
	try {
		const res = await authFetch<{ appointments: AppointmentItem[] }>('/api/area-riservata/appointments', {
			query: { from, to },
		});
		const list = res?.appointments ?? [];
		if (thisFetchId === appointmentsFetchId) {
			appointments.value = list;
		}
	} catch {
		if (thisFetchId === appointmentsFetchId) {
			appointments.value = [];
		}
	} finally {
		if (thisFetchId === appointmentsFetchId && !silent) {
			appointmentsLoading.value = false;
		}
	}
}

/** Imposta selectedDate: primo giorno con appuntamenti nel periodo visibile, oppure oggi se nel range, altrimenti primo del periodo. */
function resetSelectedDateToVisibleRange() {
	const prevYmd = toLocalYMD(selectedDate.value);
	const start = new Date(calendarStart.value);
	const startYmd = toLocalYMD(start);
	const end = new Date(start);
	end.setDate(start.getDate() + 13);
	const endYmd = toLocalYMD(end);
	// Date (YYYY-MM-DD) con appuntamenti nel periodo visibile, ordinate
	const datesWithApts = appointments.value
		.map(a => a.start_datetime.slice(0, 10))
		.filter(ymd => ymd >= startYmd && ymd <= endYmd);
	const firstWithApt = [...new Set(datesWithApts)].sort()[0];
	if (firstWithApt) {
		const [y, m, d] = firstWithApt.split('-').map(Number);
		selectedDate.value = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
		return;
	}
	// Nessun appuntamento nel periodo: oggi se nel range, altrimenti primo giorno del periodo
	const today = new Date();
	const todayYmd = toLocalYMD(today);
	if (todayYmd >= startYmd && todayYmd <= endYmd) {
		selectedDate.value = new Date(today);
	} else {
		selectedDate.value = new Date(start);
	}
}

/** Dopo l'animazione di scorrimento: ricentra il buffer sulle 2 settimane ora visibili e ricarica l'API (6 settimane). */
function recenterCalendar() {
	const visibleStart = new Date(bufferStart.value);
	visibleStart.setDate(bufferStart.value.getDate() + visibleBlockIndex.value * 14);
	const newBufferStart = new Date(visibleStart);
	newBufferStart.setDate(visibleStart.getDate() - 14);
	newBufferStart.setHours(0, 0, 0, 0);
	const newBufferEnd = new Date(newBufferStart);
	newBufferEnd.setDate(newBufferStart.getDate() + 41);
	newBufferEnd.setHours(23, 59, 59, 999);
	// Disabilita transizione e aspetta che sia applicata al DOM prima di aggiornare buffer/transform
	skipCalendarTransition.value = true;
	nextTick(() => {
		// transition:none è ora nel DOM; nel prossimo frame aggiorniamo (nessuna animazione)
		requestAnimationFrame(() => {
			bufferStart.value = newBufferStart;
			visibleBlockIndex.value = 1;
			loadAppointments(
				{ from: toLocalYMD(newBufferStart), to: toLocalYMD(newBufferEnd) },
				{ silent: true }
			);
			nextTick(() => {
				requestAnimationFrame(() => {
					skipCalendarTransition.value = false;
				});
			});
		});
	});
}

function onCalendarScrollEnd() {
	if (visibleBlockIndex.value === 0 || visibleBlockIndex.value === 2) {
		recenterCalendar();
	}
}

function onCalendarStripTransitionEnd(e: TransitionEvent) {
	if (e.propertyName !== 'transform') return;
	// Aggiorna la data selezionata (e la card) solo a animazione finita, per evitare flicker
	resetSelectedDateToVisibleRange();
	if (scrollEndTimeoutId) clearTimeout(scrollEndTimeoutId);
	if (visibleBlockIndex.value === 0 || visibleBlockIndex.value === 2) {
		scrollEndTimeoutId = setTimeout(() => {
			onCalendarScrollEnd();
			scrollEndTimeoutId = null;
		}, RECENTER_DELAY_MS);
	}
}

function prevWeeks() {
	if (visibleBlockIndex.value <= 0) return;
	if (scrollEndTimeoutId) {
		clearTimeout(scrollEndTimeoutId);
		scrollEndTimeoutId = null;
	}
	visibleBlockIndex.value--;
}

function nextWeeks() {
	if (visibleBlockIndex.value >= 2) return;
	if (scrollEndTimeoutId) {
		clearTimeout(scrollEndTimeoutId);
		scrollEndTimeoutId = null;
	}
	visibleBlockIndex.value++;
}

/** weekIndex: 0 = prima riga visibile, 1 = seconda riga visibile (nel blocco corrente). */
function selectDay(day: { date: Date }, _weekIndex: number) {
	selectedDate.value = new Date(day.date);
}

/** Titolo della card in uppercase (titolo obiettivo, non il percorso). */
function goalCardTitle(goal: GoalItem): string {
	const t = goal.title?.trim();
	return t ? String(t).toUpperCase() : 'OBIETTIVO';
}

function goalPathColor(goal: GoalItem): string {
	const path = goal.path;
	if (!path || typeof path === 'number') return '#0d9488';
	const c = (path as GoalPath).color;
	if (c && String(c).trim()) {
		const hex = String(c).trim();
		return hex.startsWith('#') ? hex : `#${hex}`;
	}
	const defaults = ['#0d9488', '#6366f1', '#84cc16'];
	const idx = goal.id % defaults.length;
	return defaults[idx] ?? '#0d9488';
}

function goalSubtitle(goal: GoalItem): string {
	return goal.description?.trim() ?? '—';
}

function goalPercentage(goal: GoalItem): number {
	const p = goal.completion_percentage;
	if (p != null && typeof p === 'number') return Math.min(100, Math.max(0, p));
	return 0;
}

function goalIcon(goal: GoalItem): string {
	const name = goal.icon?.trim();
	return name || 'flag';
}

function formatGoalDate(iso: string | null | undefined): string {
	if (!iso) return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(iso: string): string {
	const d = new Date(iso);
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Data in formato lungo per il drawer (es. "lunedì 10 marzo 2025"). */
function formatAppointmentDate(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function staffName(apt: AppointmentItem): string {
	const s = apt.staff;
	if (!s || typeof s === 'number') return '—';
	const name = [s.name, s.surname].filter(Boolean).join(' ');
	return name || '—';
}

function appointmentTypeLabel(type: string | null | undefined): string {
	if (!type) return 'Appuntamento';
	const labels: Record<string, string> = {
		workout: 'Allenamento',
		'nutrition-appointment': 'Visita Nutrizione',
		'health-appointment': 'Visita Medica',
		'mental-health-appointment': 'Visita Psicoterapia',
		'fitness-appointment': 'Visita Fitness',
		vacation: 'Ferie',
		'time-off': 'Permesso',
	};
	return labels[type] || type;
}

/** Icona Material per la tipologia di appuntamento. */
function appointmentTypeIcon(type: string | null | undefined): string {
	if (!type) return 'event';
	const icons: Record<string, string> = {
		workout: 'sports_gymnastics',
		'nutrition-appointment': 'nutrition',
		'health-appointment': 'health_and_safety',
		'mental-health-appointment': 'psychology_alt',
		'fitness-appointment': 'sports_kabaddi',
		vacation: 'beach_access',
		'time-off': 'event_busy',
	};
	return icons[type] ?? 'event';
}

/** Colore icona per la tipologia di appuntamento. */
function appointmentTypeColor(type: string | null | undefined): string {
	if (!type) return '#6b7280';
	const colors: Record<string, string> = {
		workout: '#FFA439',
		'nutrition-appointment': '#2ECDA7',
		'health-appointment': '#E35169',
		'mental-health-appointment': '#3399FF',
		'fitness-appointment': '#FFA439',
		vacation: '#A2B5CD',
		'time-off': '#A2B5CD',
	};
	return colors[type] ?? '#6b7280';
}

/** Colore bordo card: hex dall'appuntamento o default. */
function appointmentBorderColor(apt: AppointmentItem): string {
	const c = apt.color?.trim();
	if (c) return c.startsWith('#') ? c : `#${c}`;
	return '#0d9488';
}

/** URL avatar staff (public_avatar). */
function staffAvatarUrl(apt: AppointmentItem): string | null {
	const s = apt.staff;
	if (!s || typeof s === 'number') return null;
	const file = (s as { public_avatar?: string | { id: string } | null }).public_avatar;
	if (!file) return null;
	const id = typeof file === 'string' ? file : file?.id;
	if (!id) return null;
	const base = config.public.directusUrl as string;
	return base ? `${base}/assets/${id}` : null;
}

/** Orario fine per il cliente (HH:MM): customer_end_datetime se presente, altrimenti end_datetime. */
function appointmentEndTime(apt: AppointmentItem): string | null {
	const iso = apt.customer_end_datetime ?? apt.end_datetime;
	if (!iso) return null;
	return formatTime(iso);
}

/** Etichetta formato (in-person, phone, online). */
function formatLabel(fmt: string | null | undefined): string {
	if (!fmt) return '';
	const labels: Record<string, string> = {
		'in-person': 'In presenza',
		phone: 'Telefono',
		online: 'Online',
	};
	return labels[fmt] ?? fmt;
}

/** Nome del servizio erogato (da purchased_service). */
function serviceName(apt: AppointmentItem): string {
	const ps = apt.purchased_service;
	if (!ps || typeof ps === 'number') return '';
	const svc = (ps as { service?: { name?: string | null } | null }).service;
	if (!svc || typeof svc === 'number') return '';
	return (svc as { name?: string | null }).name?.trim() ?? '';
}

/** Testo quantità usata/acquistata per il servizio, es. "(2/10)". */
function serviceQuantityLabel(apt: AppointmentItem): string {
	const ps = apt.purchased_service;
	if (!ps || typeof ps === 'number') return '';
	const used = (ps as { used_quantity?: number | null }).used_quantity;
	const purchased = (ps as { purchased_quantity?: number | null }).purchased_quantity;
	if (purchased == null && used == null) return '';
	const u = used != null ? Number(used) : 0;
	const p = purchased != null ? Number(purchased) : 0;
	return `(${u}/${p})`;
}

/** Icona Material per la modalità (formato). */
function formatIcon(fmt: string | null | undefined): string {
	if (!fmt) return 'event_available';
	const icons: Record<string, string> = {
		'in-person': 'home',
		phone: 'settings_phone',
		online: 'dataset_linked',
	};
	return icons[fmt] ?? 'event_available';
}

/** Nome location (oggetto o id). */
function locationName(apt: AppointmentItem): string {
	const loc = apt.location;
	if (!loc || typeof loc === 'number') return '';
	return (loc as { name?: string | null }).name?.trim() ?? '';
}

/** URL Google Maps per la location: indirizzo se disponibile, altrimenti ricerca per nome sede. */
function locationMapsUrl(apt: AppointmentItem): string | null {
	const loc = apt.location;
	if (!loc || typeof loc === 'number') return null;
	const name = (loc as { name?: string | null }).name?.trim();
	const addr = (loc as { address?: { street?: string | null; city?: { name?: string | null } | null } | null }).address;
	const street = addr?.street?.trim();
	const cityName = typeof addr?.city === 'object' && addr?.city !== null
		? (addr.city as { name?: string | null }).name?.trim()
		: '';
	const query = [street, cityName].filter(Boolean).join(', ') || name;
	if (!query) return null;
	return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

const copyFeedback = ref<string | null>(null);
function copyOnlineUrl(url: string) {
	if (!process.client || !url) return;
	try {
		navigator.clipboard.writeText(url);
		copyFeedback.value = url;
		setTimeout(() => { copyFeedback.value = null; }, 2000);
	} catch {
		// fallback: open in new tab
		window.open(url, '_blank');
	}
}

// Drawer appuntamento: card cliccabile → pannello laterale con dettagli e Annulla
const selectedAppointment = ref<AppointmentItem | null>(null);
const cancelLoading = ref(false);
const cancelError = ref<string | null>(null);
function openAppointmentDrawer(apt: AppointmentItem) {
	selectedAppointment.value = apt;
	cancelError.value = null;
}
function closeAppointmentDrawer() {
	selectedAppointment.value = null;
	cancelError.value = null;
}
async function cancelAppointment(apt: AppointmentItem) {
	cancelLoading.value = true;
	cancelError.value = null;
	try {
		await authFetch(`/api/area-riservata/appointments/${apt.id}`, {
			method: 'PATCH',
			body: { status: 'canceled' },
		});
		appointments.value = appointments.value.filter(a => a.id !== apt.id);
		closeAppointmentDrawer();
	} catch (e: any) {
		cancelError.value = e?.data?.statusMessage || e?.message || 'Errore durante l\'annullamento';
	} finally {
		cancelLoading.value = false;
	}
}

/** Formato data/ora per .ics (UTC): YYYYMMDDTHHMMSSZ (toISOString già termina con Z) */
function toIcsUtc(iso: string): string {
	const d = new Date(iso);
	return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** Scarica un file .ics con i dati dell'appuntamento (aggiungi a calendario). */
function downloadAppointmentIcs(apt: AppointmentItem) {
	const summary = serviceName(apt)
		? `${appointmentTypeLabel(apt.type)} : ${serviceName(apt)}`
		: appointmentTypeLabel(apt.type);
	const descParts = [staffName(apt), formatLabel(apt.format), locationName(apt), apt.online_url].filter(Boolean);
	const description = descParts.join(' | ');
	const location = locationName(apt) || '';
	const dtStart = toIcsUtc(apt.start_datetime);
	const endIso = apt.customer_end_datetime ?? apt.end_datetime ?? apt.start_datetime;
	const dtEnd = toIcsUtc(endIso);
	const uid = `wefocus-apt-${apt.id}-${apt.start_datetime.slice(0, 10)}`;
	const dtStamp = toIcsUtc(new Date().toISOString());
	const ics = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//WeFocus//Area Riservata//IT',
		'CALSCALE:GREGORIAN',
		'BEGIN:VEVENT',
		`UID:${uid}`,
		`DTSTAMP:${dtStamp}`,
		`DTSTART:${dtStart}`,
		`DTEND:${dtEnd}`,
		`SUMMARY:${summary.replace(/\n/g, ' ')}`,
		...(description ? [`DESCRIPTION:${description.replace(/\n/g, ' ').replace(/,/g, '\\,')}`] : []),
		...(location ? [`LOCATION:${location.replace(/\n/g, ' ')}`] : []),
		...(apt.online_url ? [`URL:${apt.online_url}`] : []),
		'END:VEVENT',
		'END:VCALENDAR',
	].join('\r\n');
	const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `appuntamento-${apt.id}-${apt.start_datetime.slice(0, 10)}.ics`;
	a.click();
	URL.revokeObjectURL(url);
}

onMounted(() => {
	loadGoals();
	loadAppointments();
});
</script>

<template>
	<div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		<!-- Header: logo + CTA vai al sito -->
		<div class="flex items-center justify-between gap-4 mb-10">
			<div class="flex items-center">
				<img
					:src="dashboardLogoUrl"
					alt=""
					class="h-16 object-contain object-left"
				>
			</div>
			<NuxtLink
				to="/"
				class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-sm font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase whitespace-nowrap"
			>
				<span class="material-symbols-outlined text-lg">open_in_new</span>
				<span class="sm:hidden">Vai al sito</span>
				<span class="hidden sm:inline">Vai al sito WeFocus</span>
			</NuxtLink>
		</div>
		<h1 v-if="displayName" class="text-2xl font-bold text-gray-900 mb-8">
			{{ welcomeWord }} {{ displayName }}
		</h1>

		<!-- Obiettivi -->
		<section class="mb-8">
			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">I TUOI OBIETTIVI</h2>
			<div v-if="goalsLoading" class="flex items-center justify-center py-8 text-gray-500">
				<span class="material-symbols-outlined animate-spin text-3xl">progress_activity</span>
			</div>
			<div v-else-if="goals.length === 0" class="bg-white p-6 text-center text-gray-500">
				Nessun obiettivo al momento.
			</div>
			<template v-else>
				<div v-if="goalsInProgress.length === 0 && hasCompletedGoals" class="bg-white p-4 text-center text-gray-500 text-sm mb-2">
					Nessun obiettivo in corso.
				</div>
				<div class="grid gap-4 sm:grid-cols-2">
					<article
						v-for="goal in goalsInProgress"
						:key="goal.id"
						class="bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
					>
						<div class="flex items-start gap-3">
							<span class="material-symbols-outlined text-2xl text-gray-800 shrink-0" aria-hidden="true">
								{{ goalIcon(goal) }}
							</span>
							<div class="min-w-0 flex-1">
								<p class="text-xs font-semibold uppercase tracking-wide text-gray-900">
									{{ goalCardTitle(goal) }}
								</p>
								<p class="mt-0.5 text-sm text-gray-700 line-clamp-2">
									{{ goalSubtitle(goal) }}
								</p>
								<p v-if="formatGoalDate(goal.expected_completion_date)" class="mt-1 text-xs text-gray-500">
									Data prevista: {{ formatGoalDate(goal.expected_completion_date) }}
								</p>
								<div class="mt-3 flex items-center gap-2">
									<div class="h-2 flex-1 bg-gray-200 overflow-hidden">
										<div
											class="h-full transition-all duration-300"
											:style="{
												width: `${goalPercentage(goal)}%`,
												backgroundColor: goalPathColor(goal),
											}"
										/>
									</div>
									<span class="text-sm font-medium text-gray-900 tabular-nums shrink-0">
										{{ goalPercentage(goal) }}%
									</span>
								</div>
							</div>
						</div>
					</article>
				</div>
				<button
					v-if="hasCompletedGoals"
					type="button"
					class="mt-4 flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2"
					:aria-expanded="showPastGoals"
					@click="showPastGoals = !showPastGoals"
				>
					<span class="material-symbols-outlined text-lg transition-transform" :class="{ 'rotate-180': showPastGoals }">expand_more</span>
					{{ showPastGoals ? 'Nascondi obiettivi passati' : 'Visualizza obiettivi passati' }}
				</button>
				<div
					v-show="hasCompletedGoals"
					class="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
					:style="{
						maxHeight: showPastGoals ? '2000px' : '0',
						opacity: showPastGoals ? 1 : 0,
					}"
				>
					<template v-if="hasCompletedGoals">
						<h3 class="mt-6 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Obiettivi raggiunti</h3>
						<div class="grid gap-4 sm:grid-cols-2">
							<article
								v-for="goal in goalsCompleted"
								:key="goal.id"
								class="bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
							>
								<div class="flex items-start gap-3">
									<span class="material-symbols-outlined text-2xl text-gray-800 shrink-0" aria-hidden="true">
										{{ goalIcon(goal) }}
									</span>
									<div class="min-w-0 flex-1">
										<p class="text-xs font-semibold uppercase tracking-wide text-gray-900">
											{{ goalCardTitle(goal) }}
										</p>
										<p class="mt-0.5 text-sm text-gray-700 line-clamp-2">
											{{ goalSubtitle(goal) }}
										</p>
										<p v-if="formatGoalDate(goal.expected_completion_date)" class="mt-1 text-xs text-gray-500">
											Data prevista: {{ formatGoalDate(goal.expected_completion_date) }}
										</p>
										<div class="mt-3 flex items-center gap-2">
											<div class="h-2 flex-1 bg-gray-200 overflow-hidden">
												<div
													class="h-full transition-all duration-300"
													:style="{
														width: `${goalPercentage(goal)}%`,
														backgroundColor: goalPathColor(goal),
													}"
												/>
											</div>
											<span class="text-sm font-medium text-gray-900 tabular-nums shrink-0">
												{{ goalPercentage(goal) }}%
											</span>
										</div>
									</div>
								</div>
							</article>
						</div>
					</template>
				</div>
			</template>
		</section>

		<!-- Calendario settimanale + Calendario -->
		<section class="border border-gray-200 bg-white overflow-hidden">
			<!-- Intestazione calendario -->
			<div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
				<div class="flex items-center gap-2">
					<span class="material-symbols-outlined text-gray-600">calendar_month</span>
					<span class="text-sm font-medium text-gray-900">{{ calendarTitle }}</span>
				</div>
				<div class="flex items-center gap-2">
					<button
						type="button"
						class="p-2 text-gray-600 hover:bg-gray-100 transition-colors"
						aria-label="Settimane precedenti"
						@click="prevWeeks"
					>
						<span class="material-symbols-outlined">chevron_left</span>
					</button>
					<button
						type="button"
						class="p-2 text-gray-600 hover:bg-gray-100 transition-colors"
						aria-label="Settimane successive"
						@click="nextWeeks"
					>
						<span class="material-symbols-outlined">chevron_right</span>
					</button>
					<NuxtLink
						to="/area-riservata/appuntamenti"
						class="inline-flex items-center justify-center px-4 py-2 bg-black text-white text-sm font-medium hover:opacity-90 transition-opacity"
					>
						VAI AL CALENDARIO
					</NuxtLink>
				</div>
			</div>

			<!-- Griglia 6 settimane in buffer, visibili solo le 2 centrali con scorrimento fluido -->
				<div class="p-4 overflow-hidden">
				<div class="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
					<span v-for="label in WEEKDAY_LABELS" :key="label">{{ label }}</span>
				</div>
				<!-- Viewport: altezza = 2 righe di celle quadrate (aspect 7:2); striscia 6 righe, translateY in % -->
				<div class="w-full overflow-hidden aspect-[7/2] min-h-24">
					<div
						ref="calendarStripRef"
						class="duration-300 ease-out transition-transform w-full"
						:class="{ '!transition-none': skipCalendarTransition }"
						:style="{
							transform: `translateY(-${visibleBlockIndex * (100 / 3)}%)`,
							...(skipCalendarTransition ? { transition: 'none' } : {}),
						}"
						@transitionend="onCalendarStripTransitionEnd"
					>
						<div
							v-for="(week, wi) in calendarAllWeeks"
							:key="wi"
							class="grid grid-cols-7 gap-1 mb-1 w-full"
						>
							<button
								v-for="day in week"
								:key="day.date.toISOString()"
								type="button"
								class="relative w-full aspect-square text-sm font-medium transition-colors flex items-center justify-center border-2 text-gray-900 min-w-0"
								:class="{
									'border-black bg-white': day.isSelected && !day.firstColor,
									'bg-white': day.isSelected && day.firstColor,
									'border-transparent': !day.isSelected && !day.isToday && (day.hasAppointments ? day.firstColor : true),
									'bg-[#0d9488]/20': !day.isSelected && day.hasAppointments && !day.firstColor,
									'bg-gray-100 hover:bg-gray-200': !day.isSelected && !day.hasAppointments,
									'border-double': day.isToday,
									'!border-[5px]': day.isToday,
									'border-black': day.isToday && !day.isSelected,
								}"
								:style="day.firstColor && (day.isSelected || day.hasAppointments) ? { borderColor: (day.isToday && !day.isSelected) ? 'black' : (day.isSelected ? day.firstColor : 'transparent'), backgroundColor: day.isSelected ? `${day.firstColor}18` : `${day.firstColor}20` } : {}"
								@click="selectDay(day, wi % 2)"
							>
								<span>{{ day.dayNum }}</span>
								<!-- Box tipologie appuntamenti: da mobile solo quadratini colorati; da sm icona + numero -->
								<div
									v-if="day.appointmentsByType.length"
									class="absolute bottom-0.5 right-0.5 flex flex-wrap justify-end gap-0.5 max-w-full"
								>
									<div
										v-for="item in day.appointmentsByType"
										:key="item.type"
										class="flex items-center justify-center w-[6px] h-[6px] sm:min-w-[22px] sm:min-h-[22px] sm:w-auto sm:h-auto sm:px-1 sm:py-1 p-0 text-white shrink-0"
										:style="{ backgroundColor: item.color }"
										:title="appointmentTypeLabel(item.type === 'default' ? null : item.type)"
									>
										<span class="inline max-sm:!hidden material-symbols-outlined text-[20px] leading-none select-none" style="font-size: 18px" aria-hidden="true">{{ item.icon }}</span>
										<span v-if="item.count > 1" class="inline max-sm:!hidden ml-0.5 text-[12px] font-semibold leading-none tabular-nums">{{ item.count }}</span>
									</div>
								</div>
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Lista appuntamenti del giorno selezionato (v-memo riduce flicker al cambio settimana/recenter) -->
			<div v-memo="selectedDayCardMemoKey" class="border-t border-gray-200 px-4 py-4">
				<h2 class="text-sm font-semibold text-gray-900 mb-3">{{ selectedDayLabel }}</h2>
				<div v-if="appointmentsLoading" class="flex items-center gap-2 py-4 text-gray-500 text-sm">
					<span class="material-symbols-outlined animate-spin">progress_activity</span>
					Caricamento...
				</div>
				<div v-else-if="appointmentsForSelectedDay.length === 0" class="py-4 text-gray-500 text-sm">
					Nessun appuntamento in programma.
				</div>
				<ul v-else class="space-y-4">
					<li
						v-for="apt in appointmentsForSelectedDay"
						:key="apt.id"
						role="button"
						tabindex="0"
						class="border-l-4 bg-white shadow-sm overflow-hidden transition-shadow hover:shadow-md cursor-pointer"
						:style="{ borderLeftColor: appointmentBorderColor(apt) }"
						@click="openAppointmentDrawer(apt)"
						@keydown.enter="openAppointmentDrawer(apt)"
						@keydown.space.prevent="openAppointmentDrawer(apt)"
					>
						<div class="p-4 flex flex-col sm:flex-row sm:items-stretch gap-4">
							<!-- Icona tipologia + avatar -->
							<div class="flex items-center gap-3 shrink-0 sm:flex-col sm:flex-col-reverse sm:w-16 sm:items-center sm:self-stretch sm:gap-0">
								<span
									class="material-symbols-outlined text-2xl shrink-0 sm:text-3xl sm:mt-2"
									:style="{ color: appointmentTypeColor(apt.type) }"
									aria-hidden="true"
								>
									{{ appointmentTypeIcon(apt.type) }}
								</span>
								<div class="flex items-center justify-center py-2 sm:py-0 sm:flex-1 sm:min-h-0">
									<div class="w-12 h-12 rounded-full overflow-hidden bg-gray-100 ring-2 ring-gray-200 shrink-0">
										<img
											v-if="staffAvatarUrl(apt)"
											:src="staffAvatarUrl(apt)!"
											:alt="staffName(apt)"
											class="w-full h-full object-cover"
										>
										<span
											v-else
											class="w-full h-full flex items-center justify-center text-gray-400"
											aria-hidden="true"
										>
											<span class="material-symbols-outlined text-2xl">person</span>
										</span>
									</div>
								</div>
							</div>
							<div class="min-w-0 flex-1 space-y-2">
								<p class="text-sm font-semibold text-gray-900">
									<template v-if="serviceName(apt)">
										{{ appointmentTypeLabel(apt.type) }} : {{ serviceName(apt) }}
										<span v-if="serviceQuantityLabel(apt)" class="font-normal text-gray-600">{{ serviceQuantityLabel(apt) }}</span>
									</template>
									<template v-else>
										{{ appointmentTypeLabel(apt.type) }}
									</template>
								</p>
								<p class="text-sm text-gray-600">
									{{ staffName(apt) }}
								</p>
								<p class="text-sm text-gray-700 tabular-nums">
									<span class="font-medium">Dalle {{ formatTime(apt.start_datetime) }}</span>
									<template v-if="appointmentEndTime(apt)">
										<span class="text-gray-500"> – Alle </span>
										<span>{{ appointmentEndTime(apt) }}</span>
									</template>
								</p>
								<div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
									<span v-if="formatLabel(apt.format)" class="inline-flex items-center gap-1">
										<span class="material-symbols-outlined text-base">{{ formatIcon(apt.format) }}</span>
										{{ formatLabel(apt.format) }}
									</span>
									<template v-if="locationName(apt)">
										<span class="inline-flex items-center gap-1">
											<span class="material-symbols-outlined text-base">location_on</span>
											{{ locationName(apt) }}
										</span>
									</template>
								</div>
								<div v-if="apt.online_url" class="flex flex-wrap items-center gap-2">
									<span class="text-xs text-gray-500 truncate max-w-[200px] sm:max-w-xs">{{ apt.online_url }}</span>
								</div>
							</div>
						</div>
					</li>
				</ul>
			</div>
		</section>

		<!-- Drawer dettaglio appuntamento -->
		<Teleport to="body">
			<div
				v-if="selectedAppointment"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-labelledby="drawer-appointment-title"
			>
				<!-- Overlay -->
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi"
					@click="closeAppointmentDrawer"
				/>
				<!-- Pannello destro -->
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 id="drawer-appointment-title" class="text-lg font-semibold text-gray-900">
							Dettaglio appuntamento
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi"
							@click="closeAppointmentDrawer"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>
					<div v-if="selectedAppointment" class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
						<!-- Card appuntamento in sola lettura (stesso stile dell'elenco) -->
						<div
							class="border-l-4 bg-white shadow-sm overflow-hidden"
							:style="{ borderLeftColor: appointmentBorderColor(selectedAppointment) }"
						>
							<div class="p-4 flex flex-col sm:flex-row sm:items-stretch gap-4">
								<div class="flex items-center gap-3 shrink-0 sm:flex-col sm:flex-col-reverse sm:w-16 sm:items-center sm:self-stretch sm:gap-0">
									<span
										class="material-symbols-outlined text-2xl shrink-0 sm:text-3xl sm:mt-2"
										:style="{ color: appointmentTypeColor(selectedAppointment.type) }"
										aria-hidden="true"
									>
										{{ appointmentTypeIcon(selectedAppointment.type) }}
									</span>
									<div class="flex items-center justify-center py-2 sm:py-0 sm:flex-1 sm:min-h-0">
										<div class="w-12 h-12 rounded-full overflow-hidden bg-gray-100 ring-2 ring-gray-200 shrink-0">
											<img
												v-if="staffAvatarUrl(selectedAppointment)"
												:src="staffAvatarUrl(selectedAppointment)!"
												:alt="staffName(selectedAppointment)"
												class="w-full h-full object-cover"
											>
											<span
												v-else
												class="w-full h-full flex items-center justify-center text-gray-400"
												aria-hidden="true"
											>
												<span class="material-symbols-outlined text-2xl">person</span>
											</span>
										</div>
									</div>
								</div>
								<div class="min-w-0 flex-1 space-y-2">
									<p class="text-sm font-semibold text-gray-900">
										<template v-if="serviceName(selectedAppointment)">
											{{ appointmentTypeLabel(selectedAppointment.type) }} : {{ serviceName(selectedAppointment) }}
											<span v-if="serviceQuantityLabel(selectedAppointment)" class="font-normal text-gray-600">{{ serviceQuantityLabel(selectedAppointment) }}</span>
										</template>
										<template v-else>
											{{ appointmentTypeLabel(selectedAppointment.type) }}
										</template>
									</p>
									<p class="text-sm text-gray-600">
										{{ staffName(selectedAppointment) }}
									</p>
									<p class="text-sm text-gray-700 tabular-nums">
										<span class="font-medium">Dalle {{ formatTime(selectedAppointment.start_datetime) }}</span>
										<template v-if="appointmentEndTime(selectedAppointment)">
											<span class="text-gray-500"> – Alle </span>
											<span>{{ appointmentEndTime(selectedAppointment) }}</span>
										</template>
									</p>
									<div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
										<span v-if="formatLabel(selectedAppointment.format)" class="inline-flex items-center gap-1">
											<span class="material-symbols-outlined text-base">{{ formatIcon(selectedAppointment.format) }}</span>
											{{ formatLabel(selectedAppointment.format) }}
										</span>
										<template v-if="locationName(selectedAppointment)">
											<span class="inline-flex items-center gap-1">
												<span class="material-symbols-outlined text-base">location_on</span>
												{{ locationName(selectedAppointment) }}
											</span>
										</template>
									</div>
									<div v-if="selectedAppointment.online_url" class="flex flex-wrap items-center gap-2">
										<span class="text-xs text-gray-500 truncate max-w-full">{{ selectedAppointment.online_url }}</span>
									</div>
								</div>
							</div>
						</div>
						<!-- Errore annullamento -->
						<p v-if="cancelError" class="text-sm text-red-600">{{ cancelError }}</p>
						<!-- Aggiungi a calendario (sfondo nero) -->
						<button
							type="button"
							class="w-full inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors"
							@click="downloadAppointmentIcs(selectedAppointment)"
						>
							<span class="material-symbols-outlined text-lg">event</span>
							AGGIUNGI A CALENDARIO
						</button>
						<!-- Annulla appuntamento (stile sito: senza bordi tondi, rosso palette) -->
						<button
							type="button"
							class="w-full inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors"
							:disabled="cancelLoading"
							@click="cancelAppointment(selectedAppointment)"
						>
							<span v-if="cancelLoading" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
							{{ cancelLoading ? 'ANNULLAMENTO IN CORSO...' : 'ANNULLA APPUNTAMENTO' }}
						</button>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>
